"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MemoryLockManager = void 0;
class MemoryLockManager {
    constructor() {
        this.locks = new Map();
        this.nextUid = 0;
    }
    async request(name, optionsOrCallback, maybeCallback) {
        const { options, callback } = this.parseArgs(optionsOrCallback, maybeCallback);
        const mode = options.mode ?? 'exclusive';
        const ifAvailable = options.ifAvailable ?? false;
        const signal = options.signal;
        const state = this.getOrCreateState(name);
        const uid = String(++this.nextUid);
        if (ifAvailable) {
            if (!this.canAcquire(state, mode)) {
                return callback(null);
            }
            this.addHolder(state, uid, mode);
            const lock = { name, mode };
            try {
                return await callback(lock);
            }
            finally {
                this.removeHolder(state, uid, mode);
                this.drain(state);
                this.cleanupState(name, state);
            }
        }
        if (this.canAcquire(state, mode)) {
            this.addHolder(state, uid, mode);
        }
        else {
            await this.enqueue(state, uid, mode, signal);
        }
        const lock = { name, mode };
        try {
            return await callback(lock);
        }
        finally {
            this.removeHolder(state, uid, mode);
            this.drain(state);
            this.cleanupState(name, state);
        }
    }
    async query() {
        const held = [];
        const pending = [];
        for (const [name, state] of this.locks) {
            if (state.writer !== null) {
                held.push({ name, mode: 'exclusive' });
            }
            for (const _reader of state.readers) {
                held.push({ name, mode: 'shared' });
            }
            for (const waiter of state.waitQueue) {
                pending.push({ name, mode: waiter.mode });
            }
        }
        return { held, pending };
    }
    canAcquire(state, mode) {
        if (mode === 'shared') {
            if (state.writer !== null) {
                return false;
            }
            const hasExclusiveWaiter = state.waitQueue.some((w) => w.mode === 'exclusive');
            if (hasExclusiveWaiter) {
                return false;
            }
            return true;
        }
        return state.readers.size === 0 && state.writer === null;
    }
    addHolder(state, uid, mode) {
        if (mode === 'shared') {
            state.readers.add(uid);
        }
        else {
            state.writer = uid;
        }
    }
    removeHolder(state, uid, mode) {
        if (mode === 'shared') {
            state.readers.delete(uid);
        }
        else if (state.writer === uid) {
            state.writer = null;
        }
    }
    enqueue(state, uid, mode, signal) {
        if (signal?.aborted) {
            return Promise.reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'));
        }
        return new Promise((resolve, reject) => {
            const waiter = { uid, mode, resolve, reject };
            state.waitQueue.push(waiter);
            if (signal) {
                const onAbort = () => {
                    const idx = state.waitQueue.indexOf(waiter);
                    if (idx !== -1) {
                        state.waitQueue.splice(idx, 1);
                        reject(signal.reason ?? new DOMException('The operation was aborted.', 'AbortError'));
                    }
                };
                signal.addEventListener('abort', onAbort, { once: true });
                const originalResolve = waiter.resolve;
                const patchedWaiter = waiter;
                patchedWaiter.resolve = () => {
                    signal.removeEventListener('abort', onAbort);
                    originalResolve();
                };
            }
        });
    }
    drain(state) {
        while (state.waitQueue.length > 0) {
            const next = state.waitQueue[0];
            if (!this.canAcquireForDrain(state, next.mode)) {
                break;
            }
            state.waitQueue.shift();
            this.addHolder(state, next.uid, next.mode);
            next.resolve();
            if (next.mode === 'exclusive') {
                break;
            }
        }
    }
    canAcquireForDrain(state, mode) {
        if (mode === 'shared') {
            return state.writer === null;
        }
        return state.readers.size === 0 && state.writer === null;
    }
    getOrCreateState(name) {
        let state = this.locks.get(name);
        if (!state) {
            state = { readers: new Set(), writer: null, waitQueue: [] };
            this.locks.set(name, state);
        }
        return state;
    }
    cleanupState(name, state) {
        if (state.readers.size === 0 && state.writer === null && state.waitQueue.length === 0) {
            this.locks.delete(name);
        }
    }
    parseArgs(optionsOrCallback, maybeCallback) {
        if (typeof optionsOrCallback === 'function') {
            return { options: {}, callback: optionsOrCallback };
        }
        return { options: optionsOrCallback, callback: maybeCallback };
    }
}
exports.MemoryLockManager = MemoryLockManager;
//# sourceMappingURL=memory-lock-manager.js.map